import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import expenseService from "../Services/expenseService";
import axiosInstance from "../Services/axiosInstance";
import toast from "react-hot-toast";

// "Service Purchase" isn't a real expensehead row — it's sourced from the
// same /dispenser-sales/medical-services endpoint that powers the Hospital
// Services Ledger screen, so it needs its own fetch + column layout.
const isServicePurchase = (title) =>
  (title || "").trim().toLowerCase() === "service purchase";

const ExpenseDetailModal = ({ headTitle, startDate, endDate, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const serviceMode = isServicePurchase(headTitle);

  useEffect(() => {
    let isMounted = true;

    const loadExpenseHead = async () => {
      const res = await expenseService.fetchAll();
      const all = res?.expenses || [];

      let filtered = all.filter(
        (e) =>
          (e.expensehead_title || "").trim().toLowerCase() ===
          (headTitle || "").trim().toLowerCase()
      );

      if (startDate && endDate) {
        filtered = filtered.filter((e) => {
          const d = e.expense_date ? e.expense_date.slice(0, 10) : "";
          return d >= startDate && d <= endDate;
        });
      }

      // Most recent first
      filtered.sort(
        (a, b) => new Date(b.expense_date) - new Date(a.expense_date)
      );

      return filtered;
    };

    const loadServicePurchase = async () => {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(
        "/dispenser-sales/medical-services",
        { params }
      );
      const services = response.data.data || [];

      return services.map((s) => {
        const unitCost = parseFloat(s.purchase_price) || 0;
        const qty = Number(s.total_quantity_sold || 0);
        return {
          expense_id: s.id,
          item_name: s.name,
          quantity: qty,
          unit_cost: unitCost,
          amount: unitCost * qty,
        };
      });
    };

    const load = async () => {
      try {
        setLoading(true);
        setItems([]);
        const result = serviceMode
          ? await loadServicePurchase()
          : await loadExpenseHead();

        if (isMounted) setItems(result);
      } catch (error) {
        toast.error(
          serviceMode
            ? "Error fetching service purchase details"
            : "Error fetching expense details"
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [headTitle, startDate, endDate, serviceMode]);

  const fmt = (n) =>
    (parseFloat(n) || 0).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const total = items.reduce(
    (s, i) => s + (parseFloat(i.amount) || 0),
    0
  );

  const latestDate =
    !serviceMode && items.length > 0
      ? new Date(
          Math.max(...items.map((i) => new Date(i.expense_date)))
        ).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;

  return createPortal(
    <div
      className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen min-h-screen bg-black/50 flex justify-center items-center z-[9999] p-2 sm:p-4 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full
          max-w-full sm:max-w-[90%] md:max-w-[75%] lg:max-w-[65%]
          h-[95vh] sm:h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="bg-[#1F2937] px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0 mr-3">
            <p className="text-[10px] font-medium uppercase tracking-widest text-gray-400">
              {serviceMode ? "Service Purchase" : "Expense Records"}
            </p>

            <p className="text-white font-semibold text-sm sm:text-base leading-tight mt-0.5 truncate">
              {serviceMode
                ? startDate && endDate
                  ? `${startDate} to ${endDate}`
                  : "All time"
                : latestDate
                ? `Latest: ${latestDate}`
                : headTitle}
            </p>
          </div>

          <button
            onClick={onClose}
            className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full
              bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white
              transition-all duration-200 text-lg font-bold leading-none"
          >
            &times;
          </button>
        </div>

        {/* ── Info Strip ── */}
        <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5 sm:px-5 sm:py-3 flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-800 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {headTitle?.charAt(0) || "E"}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
              {headTitle}
            </p>

            <p className="text-[11px] text-gray-400">
              {items.length} record(s) found
            </p>
          </div>

          <span className="flex-shrink-0 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-red-50 text-red-600 border border-red-100 whitespace-nowrap">
            {serviceMode ? "Purchase" : "Expense"}
          </span>
        </div>

        {/* ── Scrollable Table ── */}
        <div className="overflow-y-auto overflow-x-auto flex-1 px-3 py-3 sm:px-5 sm:py-4">
          {loading ? (
            <div className="flex items-center justify-center py-14">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : items.length > 0 ? (
            serviceMode ? (
              <table className="w-full min-w-[520px] text-xs sm:text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                      #
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                      Service
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-right whitespace-nowrap">
                      Qty Sold
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-right whitespace-nowrap">
                      Purchase Price
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-right whitespace-nowrap">
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={item.expense_id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-3 py-2.5 text-gray-400 font-medium whitespace-nowrap">
                        {index + 1}
                      </td>

                      <td className="px-3 py-2.5 text-gray-800 font-medium">
                        {item.item_name}
                      </td>

                      <td className="px-3 py-2.5 text-gray-600 text-right whitespace-nowrap">
                        {item.quantity}
                      </td>

                      <td className="px-3 py-2.5 text-gray-600 text-right whitespace-nowrap">
                        PKR {fmt(item.unit_cost)}
                      </td>

                      <td className="px-3 py-2.5 text-gray-800 font-semibold text-right whitespace-nowrap">
                        PKR {fmt(item.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-[520px] text-xs sm:text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                      #
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                      Date
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                      Item
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left">
                      Description
                    </th>

                    <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-right whitespace-nowrap">
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr
                      key={item.expense_id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-3 py-2.5 text-gray-400 font-medium whitespace-nowrap">
                        {index + 1}
                      </td>

                      <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                        {item.expense_date
                          ? item.expense_date.slice(0, 10)
                          : "-"}
                      </td>

                      <td className="px-3 py-2.5 text-gray-800 font-medium">
                        {item.item_name}
                      </td>

                      <td className="px-3 py-2.5 text-gray-500">
                        {item.description || "-"}
                      </td>

                      <td className="px-3 py-2.5 text-gray-800 font-semibold text-right whitespace-nowrap">
                        PKR {fmt(item.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            <div className="text-center py-10 text-gray-400 text-sm">
              No {serviceMode ? "service purchases" : "expense"} records found
              for this period.
            </div>
          )}
        </div>

        {/* ── Fixed Total Footer (outside the scroll area, always visible) ── */}
        {!loading && items.length > 0 && (
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 sm:px-5 border-t-2 border-gray-200 bg-gray-50">
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-500">
              Total
            </span>

            <span className="text-sm sm:text-base font-bold text-gray-800">
              PKR {fmt(total)}
            </span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ExpenseDetailModal;
